// ./src/components/features/CreateEventWizard/components/WizardItemRow.tsx
//
// ONE row for everything a wizard step lists (vouchers, questions, sign-up items, polls, games): an icon, the name, an
// optional second line, and Edit / Remove — all centred on one axis.
//
// ⚠ WHY IT IS SHARED. Each step first drew its own row, top-aligned: a one-line row then sat its title against the
// top edge while the 34px buttons centred themselves below it — the voucher list the owner screenshotted (2026-09-29)
// looked as if a missing description still took up room. Fixed once here, it stays fixed on every step.

import React from 'react';
import { Box, Group, Stack, Text, Paper, ActionIcon, ThemeIcon } from '@mantine/core';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { AppIcon } from '@/components/generic';

export interface WizardItemRowProps {
  /** An `AppIcon` name. */
  icon: string;
  /** The icon's colour — a Mantine colour name or a CSS colour (a voucher's own). Omit for the theme's. */
  iconColor?: string | null;
  title: string;
  /** A dimmed second line. Omitted (or empty) → the row is one line, centred on its buttons. */
  subtitle?: string | null;
  onEdit?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
  /** Extra controls under the text (a ticket's "Price rules" button). */
  children?: React.ReactNode;
  /** A drag handle's props (`@hello-pangea/dnd`) — the row then shows a grip to reorder by (custom questions). */
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

export const WizardItemRow: React.FC<WizardItemRowProps> = ({
  icon,
  iconColor,
  title,
  subtitle,
  onEdit,
  onRemove,
  disabled = false,
  children,
  dragHandleProps,
}) => {
  const { t } = useTranslation(['common']);
  // A Mantine colour name tints the ThemeIcon; anything else (a stored hex) colours the glyph itself.
  const isThemeColor = !!iconColor && /^[a-z]+$/.test(iconColor);

  return (
    <Paper withBorder radius="md" p="sm">
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap="sm" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          {dragHandleProps ? (
            <Box {...dragHandleProps} c="dimmed" style={{ cursor: 'grab', display: 'flex' }} aria-label={t('common:actions.dragToReorder', { defaultValue: 'Drag to reorder' })}>
              <AppIcon iconName="GripVertical" size={20} />
            </Box>
          ) : null}
          <ThemeIcon variant="light" size={36} radius="md" color={isThemeColor ? (iconColor as string) : undefined}>
            <AppIcon iconName={icon} size={18} color={!isThemeColor && iconColor ? iconColor : undefined} />
          </ThemeIcon>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="md" fw={600}>
              {title}
            </Text>
            {subtitle?.trim() ? (
              <Text size="md" c="dimmed" lineClamp={2}>
                {subtitle}
              </Text>
            ) : null}
            {children}
          </Stack>
        </Group>
        <Group gap={4} wrap="nowrap">
          {onEdit ? (
            <ActionIcon variant="subtle" size="lg" aria-label={t('common:actions.edit', { defaultValue: 'Edit' })} disabled={disabled} onClick={onEdit}>
              <AppIcon iconName="Pencil" size={18} />
            </ActionIcon>
          ) : null}
          {onRemove ? (
            <ActionIcon
              variant="subtle"
              color="red"
              size="lg"
              aria-label={t('common:actions.remove', { defaultValue: 'Remove' })}
              disabled={disabled}
              onClick={onRemove}
            >
              <AppIcon iconName="Trash" size={18} />
            </ActionIcon>
          ) : null}
        </Group>
      </Group>
    </Paper>
  );
};
